import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import * as yaml from 'js-yaml';
import Icon from './icons/Icon';
import unifiedGitDataService from '../services/UnifiedGitDataService';
import indexedDbFileStorage from '../utils/indexedDbFileStorage';
import { detectAllTools, getToolDisplay } from '../utils/toolDetection';
import { DEFAULT_MICROSERVICE_BRANCH } from '../constants/gitConstants';
import miaPlatformDataService from '../services/MiaPlatformDataService';
import CollaborationPanel from './CollaborationPanel';
import GitActionsButton from './GitActionsButton';
import PipelineVisualization from './PipelineVisualization';
import { useAuth } from '../context/AuthContext';
import notificationService from '../services/NotificationService';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import './UnifiedTopBar.css';

/**
 * UnifiedTopBar Component
 * A dynamic, space-efficient top bar that changes content based on active tab
 * Implements the design from the mockups: minimal when inactive, expands when active
 */
const UnifiedTopBar = ({
  activeTab,
  selectedProject,
  selectedResource,
  configuration,
  onTabChange,
  onToggleFullscreen,
  isFullscreen,
  onServiceSelect,
  onPreview,
  onRefresh,
  hasModifications,
  onPull,
  onPush,
  onPipeline,
  isPulling,
  isPushing,
  pipelineStatus,
  onNewConversation,
  onShowHistory,
  splitViewMode = 'editor',
  onSplitViewModeChange,
  previewTabs = [],
  markdownFiles = [],
  selectedMarkdownFile = null,
  onMarkdownFileSelect,
  showDashboardTab = false,
  showRequirementsTab = false
}) => {
  const { user } = useAuth();
  const [forceCleanup, setForceCleanup] = useState(false);
  const [currentBranch, setCurrentBranch] = useState(null);
  const [branches, setBranches] = useState([]);
  const [tags, setTags] = useState([]);
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);
  const [loadingRefs, setLoadingRefs] = useState(false);
  const [showCreateBranch, setShowCreateBranch] = useState(false);
  const [showCreateTag, setShowCreateTag] = useState(false);
  const [newBranchName, setNewBranchName] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [tagMessage, setTagMessage] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const branchDropdownRef = useRef(null);
  const [showDeployDropdown, setShowDeployDropdown] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [showPipelineModal, setShowPipelineModal] = useState(false);
  const [pipelineLogs, setPipelineLogs] = useState([]);
  const [currentPipeline, setCurrentPipeline] = useState(null);
  const deployDropdownRef = useRef(null);
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const viewDropdownRef = useRef(null);
  const [showCloneDropdown, setShowCloneDropdown] = useState(false);
  const cloneDropdownRef = useRef(null);
  
  // Pipeline history modal state
  const [showPipelineHistoryModal, setShowPipelineHistoryModal] = useState(false);
  const [pipelineHistory, setPipelineHistory] = useState([]);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [selectedPipelineForLogs, setSelectedPipelineForLogs] = useState(null);
  const [showPipelineLogsModal, setShowPipelineLogsModal] = useState(false);
  const [pipelineJobs, setPipelineJobs] = useState([]);
  const [pipelineConfig, setPipelineConfig] = useState(null);
  const [loadingPipelineConfig, setLoadingPipelineConfig] = useState(false);
  const [selectedJobLogs, setSelectedJobLogs] = useState('');
  const [loadingJobLogs, setLoadingJobLogs] = useState(false);
  const pipelinePollingIntervalRef = useRef(null);
  const jobsPollingIntervalRef = useRef(null);
  const previousPipelineStatusRef = useRef(null);
  
  // Git Actions Button state
  const [modifiedFilesCount, setModifiedFilesCount] = useState(0);
  const [commitsToPush, setCommitsToPush] = useState(0);
  const [commitsToPull, setCommitsToPull] = useState(0);
  const [enableConsolidatedGitButton, setEnableConsolidatedGitButton] = useState(() => {
    return localStorage.getItem('ENABLE_CONSOLIDATED_GIT_BUTTON') === 'true';
  });
  
  // Tool detection state
  const [detectedTools, setDetectedTools] = useState(null);

  // Extract GitLab project ID from repository URL
  const getGitlabProjectId = (service) => {
    if (!service) return null;
    const repositoryUrl = service.repositoryUrl || service.repoUrl || service.sshUrl;
    if (!repositoryUrl) return null;
    
    try {
      const url = new URL(repositoryUrl.replace('.git', ''));
      const pathParts = url.pathname.split('/').filter(Boolean);
      return pathParts.join('/');
    } catch (err) {
      return null;
    }
  };

  const getRepositoryUrl = (service) => {
    if (!service) return null;
    return service.repositoryUrl || service.repoUrl || service.sshUrl;
  };

  /**
   * Pre-flight: check if the git provider tool is enabled and the user is authenticated.
   * Returns { toolEnabled, authenticated, providerName }.
   * Fails open (toolEnabled=true) if the tools API is unreachable, so we never
   * silently block a working integration due to a transient network issue.
   */
  const checkGitProviderStatus = async (repoUrl) => {
    if (!repoUrl) return { toolEnabled: false, authenticated: false, providerName: 'Git' };
    try {
      const provider = unifiedGitDataService.detectProvider(repoUrl);
      const providerName = provider === 'github' ? 'GitHub' : provider === 'azuredevops' ? 'Azure DevOps' : 'GitLab';

      // 1. Tool enablement (fail-open on network error)
      let toolEnabled = true;
      try {
        const { getBffHeaders } = await import('../utils/apiHeaders');
        const { default: cfg } = await import('../config');
        const bffUrl = cfg.bffUrl || window.location.origin;
        const storedTenant = localStorage.getItem('mia_selectedTenant');
        const tenantId = storedTenant ? JSON.parse(storedTenant).tenantId : null;
        const toolsParams = tenantId ? `?tenantId=${tenantId}` : '';
        const res = await getAuthorizedFetch()(`${bffUrl}/api/catalog/ai-tools${toolsParams}`, { headers: getBffHeaders() });
        if (res.ok) {
          const data = await res.json();
          const allTools = data?.data?.items || [];
          const providerTools = allTools.filter(t => {
            const cat = (t.category || '').toLowerCase();
            const name = (t.name || '').toLowerCase();
            const runtimeName = (t.runtimeName || '').toLowerCase();
            if (provider === 'github') return cat.includes('github') || name.includes('github') || runtimeName.includes('github');
            if (provider === 'azuredevops') return cat.includes('azure') || name.includes('azure') || runtimeName.includes('azure_devops');
            return cat.includes('gitlab') || name.includes('gitlab') || runtimeName.includes('gitlab');
          });
          if (providerTools.length > 0) toolEnabled = providerTools.some(t => t.enabled !== false);
        }
      } catch { toolEnabled = true; } // fail-open

      // 2. Authentication
      const authStatus = await unifiedGitDataService.checkAuthentication(provider);
      return { toolEnabled, authenticated: authStatus.authenticated, providerName };
    } catch {
      return { toolEnabled: true, authenticated: false, providerName: 'Git' };
    }
  };

  // Track modified files count for git actions button
  useEffect(() => {
    const updateModifiedFilesCount = async () => {
      if (!selectedProject?.id || !selectedResource?.data?.name || selectedResource.type !== 'service') {
        setModifiedFilesCount(0);
        return;
      }

      try {
        const count = await indexedDbFileStorage.getModifiedFilesCount(
          selectedProject.id,
          selectedResource.data.name
        );
        setModifiedFilesCount(count);
      } catch (error) {
        console.error('Failed to get modified files count:', error);
        setModifiedFilesCount(0);
      }
    };

    updateModifiedFilesCount();

    // Listen for file modifications
    const handleFileModified = () => {
      updateModifiedFilesCount();
    };

    window.addEventListener('fileModified', handleFileModified);
    window.addEventListener('gitPushCompleted', handleFileModified);
    window.addEventListener('gitPullCompleted', handleFileModified);

    return () => {
      window.removeEventListener('fileModified', handleFileModified);
      window.removeEventListener('gitPushCompleted', handleFileModified);
      window.removeEventListener('gitPullCompleted', handleFileModified);
    };
  }, [selectedProject, selectedResource]);

  // Detect tools when service changes
  useEffect(() => {
    const detectServiceTools = async () => {
      if (!selectedResource || selectedResource.type !== 'service') {
        setDetectedTools(null);
        return;
      }

      try {
        // Get source code from IndexedDB
        const tenantId = localStorage.getItem('mia_selectedTenant') ? JSON.parse(localStorage.getItem('mia_selectedTenant')).tenantId : null;
        const projectId = selectedProject?.id;
        const serviceName = selectedResource.data?.name;

        if (!tenantId || !projectId || !serviceName) return;

        const files = await indexedDbFileStorage.getAllFiles(projectId, serviceName);
        const sourceCode = {};
        
        files.forEach(file => {
          sourceCode[file.path] = file.content;
        });

        // Detect tools from service and source code
        const tools = detectAllTools(selectedResource.data, sourceCode);
        setDetectedTools(tools);
        
        console.log('🔍 Detected tools:', tools); /* eslint-disable-line no-console */
      } catch (error) {
        console.error('Failed to detect tools:', error); /* eslint-disable-line no-console */
        setDetectedTools(null);
      }
    };

    detectServiceTools();
  }, [selectedProject, selectedResource]);

  // Fetch branches and tags when service changes
  useEffect(() => {
    const loadGitRefs = async () => {
      if (!selectedResource || selectedResource.type !== 'service') {
        setBranches([]);
        setTags([]);
        setCurrentBranch(null);
        return;
      }

      const gitlabProjectId = getGitlabProjectId(selectedResource.data);
      if (!gitlabProjectId) return;

      const repositoryUrl = getRepositoryUrl(selectedResource.data);
      if (!repositoryUrl) return;

      // ── PRE-FLIGHT: check tool enabled + authenticated BEFORE any git API call ──
      const gitStatus = await checkGitProviderStatus(repositoryUrl);
      if (!gitStatus.toolEnabled || !gitStatus.authenticated) {
        // eslint-disable-next-line no-console
        console.warn(`🚫 [TopBar] Skipping git refs load — ${gitStatus.toolEnabled ? 'not authenticated' : 'tool disabled'} for ${gitStatus.providerName}`);
        setBranches([]);
        setTags([]);
        setCurrentBranch(null);
        return;
      }
      // ─────────────────────────────────────────────────────────────────────────

      // Reset branch while loading so the pipeline effect waits for the real value
      setCurrentBranch(null);

      try {
        setLoadingRefs(true);
        
        // Fetch branches and tags separately to handle errors gracefully
        const branchList = await unifiedGitDataService.listBranches(gitlabProjectId, repositoryUrl);
        setBranches(branchList);
        
        // Try to fetch tags, but don't fail if endpoint doesn't exist
        try {
          const tagList = await unifiedGitDataService.listTags(gitlabProjectId, repositoryUrl);
          setTags(tagList);
        } catch (tagError) {
          console.log('Tags not available (this is normal for some GitLab instances):', tagError.message); /* eslint-disable-line no-console */
          setTags([]);
        }

        // Check for saved ref in localStorage
        const savedRef = localStorage.getItem(`git_ref_${selectedProject?.id}_${selectedResource.data?.name}`);
        if (savedRef) {
          setCurrentBranch(savedRef);
        } else {
          // Get default branch from project info
          const projectInfo = await unifiedGitDataService.getProject(gitlabProjectId, repositoryUrl);
          setCurrentBranch(projectInfo.default_branch || DEFAULT_MICROSERVICE_BRANCH);
        }
      } catch (error) {
        console.error('Failed to load git refs:', error); /* eslint-disable-line no-console */
      } finally {
        setLoadingRefs(false);
      }
    };

    loadGitRefs();
  }, [selectedResource, selectedProject]);


  // Listen for feature flag changes
  useEffect(() => {
    const handleStorageChange = () => {
      setEnableConsolidatedGitButton(localStorage.getItem('ENABLE_CONSOLIDATED_GIT_BUTTON') === 'true');
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('featureFlagChanged', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('featureFlagChanged', handleStorageChange);
    };
  }, []);

  // Auto-select project configuration by default, or service if only one is available
  useEffect(() => {
    if (activeTab === 'preview' && configuration?.services && !selectedResource && onServiceSelect && selectedProject) {
      // Always auto-select project configuration when opening a project
      console.log('🎯 Auto-selecting project configuration');
      onServiceSelect('project-configuration');
    }
  }, [activeTab, configuration, selectedResource, onServiceSelect, selectedProject]);

  // Load initial pipeline status when component mounts or service changes
  // Also poll for updates when pipeline is running
  useEffect(() => {
    const loadPipelineStatus = async () => {
      if (!selectedResource || selectedResource.type !== 'service') {
        return;
      }

      // Wait until the real branch has been resolved from the server/localStorage
      if (!currentBranch) {
        return;
      }

      const gitlabProjectId = getGitlabProjectId(selectedResource.data);
      if (!gitlabProjectId) return;

      const repositoryUrl = getRepositoryUrl(selectedResource.data);
      if (!repositoryUrl) return;

      try {
        // Fetch latest pipeline for current branch
        const pipelines = await unifiedGitDataService.listWorkflowRuns(
          gitlabProjectId,
          repositoryUrl,
          currentBranch,
          1 // Only get the latest pipeline
        );

        if (pipelines && pipelines.length > 0) {
          const latestPipeline = pipelines[0];
          const previousStatus = previousPipelineStatusRef.current;
          const currentStatus = latestPipeline.status;
          
          // Broadcast the status so Canvas can update UnifiedTopBar prop
          window.dispatchEvent(new CustomEvent('servicePipelineStatusUpdate', {
            detail: { status: currentStatus }
          }));

          // Create notification when pipeline status changes from running/pending to success or failed
          if (previousStatus && (previousStatus === 'running' || previousStatus === 'pending')) {
            if (currentStatus === 'success' || currentStatus === 'failed') {
              const serviceName = selectedResource?.data?.name || 'Unknown Service';
              const pipelineId = latestPipeline.id || 'Unknown';
              
              try {
                await notificationService.createNotification({
                  userId: user?.userId || user?.email || 'system',
                  title: currentStatus === 'success' ? 'Pipeline Completed Successfully' : 'Pipeline Failed',
                  message: `Pipeline #${pipelineId} for ${serviceName} on branch ${currentBranch} has ${currentStatus === 'success' ? 'completed successfully' : 'failed'}.`,
                  type: currentStatus === 'success' ? 'success' : 'error',
                  priority: currentStatus === 'failed' ? 'high' : 'normal',
                  actionUrl: latestPipeline.web_url || null,
                  actionLabel: 'View Pipeline',
                  metadata: {
                    pipelineId: latestPipeline.id,
                    serviceName,
                    branch: currentBranch,
                    status: currentStatus,
                    projectId: gitlabProjectId
                  },
                  ttlDays: 7
                });
                console.log(`✅ Notification created for pipeline ${currentStatus}`);
              } catch (error) {
                console.error('Failed to create pipeline notification:', error);
              }
            }
          }
          
          // Update previous status
          previousPipelineStatusRef.current = currentStatus;

          // If pipeline is still running or pending, continue polling
          if (latestPipeline.status === 'running' || latestPipeline.status === 'pending') {
            if (!pipelinePollingIntervalRef.current) {
              pipelinePollingIntervalRef.current = setInterval(loadPipelineStatus, 5000);
            }
          } else {
            // Pipeline finished, stop polling
            if (pipelinePollingIntervalRef.current) {
              clearInterval(pipelinePollingIntervalRef.current);
              pipelinePollingIntervalRef.current = null;
            }
          }
        } else {
          // No pipeline found, stop polling
          if (pipelinePollingIntervalRef.current) {
            clearInterval(pipelinePollingIntervalRef.current);
            pipelinePollingIntervalRef.current = null;
          }
        }
      } catch (error) {
        // Silently fail - pipeline status is optional
        console.error('Failed to load pipeline status:', error); /* eslint-disable-line no-console */
      }
    };

    loadPipelineStatus();

    // Cleanup polling on unmount or when dependencies change
    return () => {
      if (pipelinePollingIntervalRef.current) {
        clearInterval(pipelinePollingIntervalRef.current);
        pipelinePollingIntervalRef.current = null;
      }
    };
  }, [selectedResource, currentBranch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (branchDropdownRef.current && !branchDropdownRef.current.contains(event.target)) {
        setShowBranchDropdown(false);
      }
      if (viewDropdownRef.current && !viewDropdownRef.current.contains(event.target)) {
        setShowViewDropdown(false);
      }
      if (deployDropdownRef.current && !deployDropdownRef.current.contains(event.target)) {
        setShowDeployDropdown(false);
      }
      if (cloneDropdownRef.current && !cloneDropdownRef.current.contains(event.target)) {
        setShowCloneDropdown(false);
      }
    };

    if (showBranchDropdown || showViewDropdown || showDeployDropdown || showCloneDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showBranchDropdown, showViewDropdown, showDeployDropdown, showCloneDropdown]);

  const handleBranchChange = async (refName, refType) => {
    const startTime = Date.now();
    
    // Don't do anything if selecting the same ref
    if (refName === currentBranch) {
      setShowBranchDropdown(false);
      return;
    }

    const projectId = selectedProject?.id;
    const serviceName = selectedResource?.data?.name;

    if (!projectId || !serviceName) {
      console.error('Missing project ID or service name'); /* eslint-disable-line no-console */
      return;
    }

    try {
      // Check for local modifications
      const hasModifications = await indexedDbFileStorage.hasModifiedFiles(projectId, serviceName);
      const modifiedCount = await indexedDbFileStorage.getModifiedFilesCount(projectId, serviceName);

      if (hasModifications) {
        // Prompt user about losing unpushed modifications
        const message = `You have ${modifiedCount} unpushed modification${modifiedCount > 1 ? 's' : ''}.\n\n` +
                       `Switching to ${refType} "${refName}" will discard all local changes.\n\n` +
                       `Do you want to proceed and lose all not pushed modifications?`;
        
        const confirmed = window.confirm(message);
        
        if (!confirmed) {
          // User cancelled - don't switch
          setShowBranchDropdown(false);
          return;
        }
      }

      // Clear IndexedDB files for this project/service
      console.log(`🗑️ Clearing IndexedDB files for ${projectId}/${serviceName}...`); /* eslint-disable-line no-console */
      await indexedDbFileStorage.clearProjectFiles(projectId, serviceName, { suppressEvent: true });

      // Update current branch
      setCurrentBranch(refName);
      setShowBranchDropdown(false);

      // Save to localStorage
      localStorage.setItem(`git_ref_${projectId}_${serviceName}`, refName);

      // Dispatch event to notify RepositoryTree and preview to pull the new ref
      window.dispatchEvent(new CustomEvent('gitRefChanged', {
        detail: {
          projectId,
          serviceName,
          ref: refName,
          refType,
          shouldPull: true  // Signal that a pull should be triggered
        }
      }));

      // Track successful branch/tag switch
      if (window.analytics?.trackAction) {
        window.analytics.trackAction('git', 'branch_switch', {
          from: currentBranch,
          to: refName,
          type: refType,
          duration: Date.now() - startTime
        });
      }
      
      // Track feature usage
      if (window.analytics?.trackFeature) {
        window.analytics.trackFeature('git_branch_management', {
          operation: 'switch',
          refType
        });
      }

      console.log(`✅ Switched to ${refType}: ${refName}`); /* eslint-disable-line no-console */
    } catch (error) {
      console.error('Error switching branch/tag:', error); /* eslint-disable-line no-console */
      
      // Track error
      if (window.analytics?.trackError) {
        window.analytics.trackError('git_error', {
          operation: 'branch_switch',
          error: error.message
        });
      }
      
      alert(`Failed to switch ${refType}: ${error.message}`);
    }
  };

  const handleCreateBranch = async () => {
    if (!newBranchName.trim()) return;

    const gitlabProjectId = getGitlabProjectId(selectedResource?.data);
    if (!gitlabProjectId) return;

    const repositoryUrl = getRepositoryUrl(selectedResource?.data);
    if (!repositoryUrl) return;

    try {
      setIsCreating(true);
      await unifiedGitDataService.createBranch(gitlabProjectId, repositoryUrl, newBranchName, currentBranch);

      // Track branch creation
      if (window.analytics?.trackAction) {
        window.analytics.trackAction('git', 'branch_create', {
          branchName: newBranchName,
          fromBranch: currentBranch
        });
      }
      
      // Refresh branches list
      const branchList = await unifiedGitDataService.listBranches(gitlabProjectId, repositoryUrl);
      setBranches(branchList);
      
      // Switch to new branch
      handleBranchChange(newBranchName, 'branch');
      
      setShowCreateBranch(false);
      setNewBranchName('');
    } catch (error) {
      console.error('Failed to create branch:', error); /* eslint-disable-line no-console */
      
      // Track error
      if (window.analytics?.trackError) {
        window.analytics.trackError('git_error', {
          operation: 'branch_create',
          error: error.message
        });
      }
      
      alert(`Failed to create branch: ${error.message}`);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;

    const gitlabProjectId = getGitlabProjectId(selectedResource?.data);
    if (!gitlabProjectId) return;

    const repositoryUrl = getRepositoryUrl(selectedResource?.data);
    if (!repositoryUrl) return;

    try {
      setIsCreating(true);
      await unifiedGitDataService.createTag(gitlabProjectId, repositoryUrl, newTagName, currentBranch, tagMessage);

      // Track tag creation
      if (window.analytics?.trackAction) {
        window.analytics.trackAction('git', 'tag_create', {
          tagName: newTagName,
          message: tagMessage
        });
      }
      
      // Refresh tags list
      try {
        const tagList = await unifiedGitDataService.listTags(gitlabProjectId, repositoryUrl);
        setTags(tagList);
      } catch (tagError) {
        console.log('Tags not available after creation'); /* eslint-disable-line no-console */
      }
      
      // Switch to new tag
      handleBranchChange(newTagName, 'tag');
      
      setShowCreateTag(false);
      setNewTagName('');
      setTagMessage('');
    } catch (error) {
      console.error('Failed to create tag:', error); /* eslint-disable-line no-console */
      
      // Track error
      if (window.analytics?.trackError) {
        window.analytics.trackError('git_error', {
          operation: 'tag_create',
          error: error.message
        });
      }
      
      alert(`Failed to create tag: ${error.message}`);
    } finally {
      setIsCreating(false);
    }
  };
  
  const handleDeploy = async (environment) => {
    if (!selectedProject?.id) {
      alert('No project selected');
      return;
    }

    const startTime = Date.now();

    // Track deployment start
    if (window.analytics?.trackDeployment) {
      window.analytics.trackDeployment('started', {
        environment: environment.envId,
        projectId: selectedProject.id,
        serviceName: selectedResource?.data?.name
      });
    }

    try {
      setIsDeploying(true);
      setShowDeployDropdown(false);

      // Use "main" as the default project configuration revision
      // Note: This is the project configuration branch, not the microservice branch
      const configRevision = 'main';
      
      const result = await miaPlatformDataService.deployProject(
        selectedProject.id,
        environment.envId,
        'revision',
        configRevision
      );
      
      // Track deployment completion
      if (window.analytics?.trackDeployment) {
        window.analytics.trackDeployment('completed', {
          environment: environment.envId,
          duration: Date.now() - startTime,
          success: true,
          pipelineId: result.pipelineId || result.id
        });
      }
      
      // Track feature usage
      if (window.analytics?.trackFeature) {
        window.analytics.trackFeature('deployment', {
          environment: environment.envId
        });
      }
      
      // Track deployment
      if (window.analytics?.trackDeployment) {
        window.analytics.trackDeployment('deploy_all', {
          projectId: selectedProject.id,
          projectName: selectedProject.name,
          environment: environment.envId,
          environmentLabel: environment.label,
          pipelineId: result.pipelineId || result.id
        });
      }

      // Store pipeline info and show modal
      setCurrentPipeline({
        id: result.pipelineId || result.id,
        url: result.url,
        projectId: selectedProject.id,
        environment: environment.envId,
        environmentLabel: environment.label || environment.envId,
        gitlabProjectId: extractGitLabProjectId(result.url)
      });
      setPipelineLogs([
        `🚀 Deployment triggered for ${environment.label || environment.envId}`,
        `📋 Pipeline ID: ${result.pipelineId || result.id}`,
        `🔗 GitLab URL: ${result.url || 'N/A'}`,
        '⏳ Fetching pipeline jobs...'
      ]);
      setShowPipelineModal(true);

      // Start polling for pipeline status and logs
      if (result.pipelineId || result.id) {
        pollPipelineStatusAndLogs(
          selectedProject.id, 
          result.pipelineId || result.id, 
          environment.envId,
          extractGitLabProjectId(result.url)
        );
      }
    } catch (error) {
      console.error('Deployment failed:', error); /* eslint-disable-line no-console */
      alert(`❌ Deployment failed: ${error.message}`);
    } finally {
      setIsDeploying(false);
    }
  };

  // Extract GitLab project ID from pipeline URL
  const extractGitLabProjectId = (url) => {
    if (!url) return null;
    // URL format: https://git.tools.mia-platform.eu/clients/mia-platform/demo/.../Configurations/-/pipelines/985199
    const match = url.match(/https?:\/\/[^\/]+\/(.+?)\/-\/pipelines/);
    return match ? match[1] : null;
  };

  const pollPipelineStatusAndLogs = async (projectId, pipelineId, environment, gitlabProjectId) => {
    let attempts = 0;
    const maxAttempts = 120; // Poll for up to 10 minutes (every 5 seconds)
    let lastJobsFetched = null;
    
    const poll = async () => {
      try {
        attempts++;
        const status = await miaPlatformDataService.getPipelineStatus(projectId, pipelineId, environment);
        
        const timestamp = new Date().toLocaleTimeString();
        let statusEmoji = '⏳';
        
        // Fetch GitLab pipeline jobs and logs if we have project ID
        if (gitlabProjectId && !lastJobsFetched) {
          try {
            const repositoryUrl = getRepositoryUrl(selectedResource?.data);
            const jobs = await unifiedGitDataService.getWorkflowJobs(gitlabProjectId, repositoryUrl, pipelineId);
            lastJobsFetched = Date.now();
            
            setPipelineLogs(prev => [
              ...prev,
              `\n📦 Found ${jobs.length} job(s):`,
              ...jobs.map(job => `  • ${job.name} [${job.status}]`)
            ]);

            // Fetch logs for each job
            for (const job of jobs) {
              if (job.status === 'running' || job.status === 'failed' || job.status === 'success') {
                try {
                  const repositoryUrl = getRepositoryUrl(selectedResource?.data);
                  const logs = await unifiedGitDataService.getJobTrace(gitlabProjectId, repositoryUrl, job.id);
                  setPipelineLogs(prev => [
                    ...prev,
                    `\n📝 Logs for ${job.name}:`,
                    `${'─'.repeat(50)}`,
                    logs || '(no logs available)',
                    `${'─'.repeat(50)}`
                  ]);
                } catch (logError) {
                  console.error(`Error fetching logs for job ${job.id}:`, logError); /* eslint-disable-line no-console */
                }
              }
            }
          } catch (jobsError) {
            console.error('Error fetching pipeline jobs:', jobsError); /* eslint-disable-line no-console */
            setPipelineLogs(prev => [...prev, `${timestamp} ⚠️ Could not fetch GitLab logs: ${jobsError.message}`]);
          }
        }
        
        // Update status
        if (status.status === 'success') {
          statusEmoji = '✅';
          setPipelineLogs(prev => [...prev, `${timestamp} ${statusEmoji} Deployment completed successfully!`]);
          
          // Create success notification for deployment
          try {
            await notificationService.createNotification({
              userId: user?.userId || user?.email || 'system',
              title: 'Deployment Completed Successfully',
              message: `Deployment to ${environment} has completed successfully. Pipeline ID: ${pipelineId}`,
              type: 'success',
              priority: 'normal',
              actionUrl: null,
              actionLabel: null,
              metadata: {
                pipelineId,
                environment,
                projectId,
                gitlabProjectId,
                type: 'deployment'
              },
              ttlDays: 7
            });
            console.log('✅ Deployment success notification created');
          } catch (error) {
            console.error('Failed to create deployment notification:', error);
          }
          
          return;
        } else if (status.status === 'failed') {
          statusEmoji = '❌';
          setPipelineLogs(prev => [...prev, `${timestamp} ${statusEmoji} Deployment failed!`]);
          
          // Create failure notification for deployment
          try {
            await notificationService.createNotification({
              userId: user?.userId || user?.email || 'system',
              title: 'Deployment Failed',
              message: `Deployment to ${environment} has failed. Pipeline ID: ${pipelineId}`,
              type: 'error',
              priority: 'high',
              actionUrl: null,
              actionLabel: null,
              metadata: {
                pipelineId,
                environment,
                projectId,
                gitlabProjectId,
                type: 'deployment'
              },
              ttlDays: 7
            });
            console.log('❌ Deployment failure notification created');
          } catch (error) {
            console.error('Failed to create deployment notification:', error);
          }
          
          // Fetch logs one more time on failure
          if (gitlabProjectId && (Date.now() - (lastJobsFetched || 0)) > 10000) {
            try {
              const repositoryUrl = getRepositoryUrl(selectedResource?.data);
              const jobs = await unifiedGitDataService.getWorkflowJobs(gitlabProjectId, repositoryUrl, pipelineId);
              for (const job of jobs) {
                if (job.status === 'failed') {
                  const repositoryUrl = getRepositoryUrl(selectedResource?.data);
                  const logs = await unifiedGitDataService.getJobTrace(gitlabProjectId, repositoryUrl, job.id);
                  setPipelineLogs(prev => [
                    ...prev,
                    `\n❌ Failed job ${job.name}:`,
                    logs || '(no logs available)'
                  ]);
                }
              }
            } catch (error) {
              console.error('Error fetching failure logs:', error); /* eslint-disable-line no-console */
            }
          }
          return;
        } else if (status.status === 'canceled') {
          statusEmoji = '🚫';
          setPipelineLogs(prev => [...prev, `${timestamp} ${statusEmoji} Deployment canceled!`]);
          return;
        } else if (status.status === 'running') {
          statusEmoji = '🔄';
        } else if (status.status === 'pending') {
          statusEmoji = '⏳';
        }
        
        setPipelineLogs(prev => {
          const newLogs = [...prev];
          // Update or add status line
          const statusLineIndex = newLogs.findIndex(log => log.includes('Status:'));
          const statusLine = `${timestamp} ${statusEmoji} Status: ${status.status}`;
          if (statusLineIndex >= 0) {
            newLogs[statusLineIndex] = statusLine;
          } else {
            newLogs.push(statusLine);
          }
          return newLogs;
        });
        
        if (attempts < maxAttempts && (status.status === 'pending' || status.status === 'running')) {
          // Continue polling
          setTimeout(poll, 5000);
        } else if (attempts >= maxAttempts) {
          setPipelineLogs(prev => [...prev, `${timestamp} ⚠️ Stopped polling after ${maxAttempts} attempts`]);
        }
      } catch (error) {
        console.error('Error polling pipeline status:', error); /* eslint-disable-line no-console */
        const timestamp = new Date().toLocaleTimeString();
        setPipelineLogs(prev => [...prev, `${timestamp} ❌ Error: ${error.message}`]);
      }
    };
    
    poll();
  };

  // Handle viewing pipeline history
  const handleViewPipelines = async () => {
    const gitlabProjectId = getGitlabProjectId(selectedResource?.data);
    if (!gitlabProjectId) {
      alert('No Git repository configured for this service');
      return;
    }

    const repositoryUrl = getRepositoryUrl(selectedResource?.data);
    if (!repositoryUrl) {
      alert('No repository URL found');
      return;
    }

    try {
      setLoadingPipelines(true);
      setPipelineHistory([]);
      setShowPipelineHistoryModal(true);

      // Fetch pipelines for current branch
      const pipelines = await unifiedGitDataService.listWorkflowRuns(
        gitlabProjectId,
        repositoryUrl,
        currentBranch
      );

      setPipelineHistory(pipelines || []);
    } catch (error) {
      console.error('Failed to load pipelines:', error); /* eslint-disable-line no-console */
      alert(`Failed to load pipelines: ${error.message}`);
    } finally {
      setLoadingPipelines(false);
    }
  };

  // Refresh pipeline history
  const handleRefreshPipelines = async () => {
    const gitlabProjectId = getGitlabProjectId(selectedResource?.data);
    if (!gitlabProjectId) {
      alert('No Git repository configured for this service');
      return;
    }

    const repositoryUrl = getRepositoryUrl(selectedResource?.data);
    if (!repositoryUrl) {
      alert('No repository URL found');
      return;
    }

    try {
      setLoadingPipelines(true);

      // Fetch pipelines for current branch
      const pipelines = await unifiedGitDataService.listWorkflowRuns(
        gitlabProjectId,
        repositoryUrl,
        currentBranch
      );

      setPipelineHistory(pipelines || []);
    } catch (error) {
      console.error('Failed to refresh pipelines:', error); /* eslint-disable-line no-console */
      alert(`Failed to refresh pipelines: ${error.message}`);
    } finally {
      setLoadingPipelines(false);
    }
  };

  // View logs for a specific pipeline
  const handleViewPipelineLogs = async (pipeline) => {
    const gitlabProjectId = getGitlabProjectId(selectedResource?.data);
    if (!gitlabProjectId) return;

    const repositoryUrl = getRepositoryUrl(selectedResource?.data);
    if (!repositoryUrl) return;

    try {
      setSelectedPipelineForLogs(pipeline);
      setPipelineJobs([]);
      setPipelineConfig(null);
      setSelectedJobLogs('');
      setShowPipelineLogsModal(true);

      // Fetch jobs for this pipeline
      const jobs = await unifiedGitDataService.getWorkflowJobs(
        gitlabProjectId,
        repositoryUrl,
        pipeline.id
      );

      setPipelineJobs(jobs || []);

      // Fetch pipeline configuration using the pipeline's ref
      const config = await fetchPipelineConfig(pipeline.ref);
      if (config) {
        setPipelineConfig(config);
      } else {
        console.warn('Could not load pipeline configuration from .gitlab-ci.yml'); /* eslint-disable-line no-console */
      }

      // Start polling if pipeline is still running or pending
      const isActive = ['running', 'pending'].includes(pipeline.status);
      if (isActive) {
        startJobsPolling(gitlabProjectId, pipeline.id);
      }
    } catch (error) {
      console.error('Failed to load pipeline jobs:', error); /* eslint-disable-line no-console */
      alert(`Failed to load pipeline jobs: ${error.message}`);
    }
  };

  // Start polling for job status updates
  const startJobsPolling = (gitlabProjectId, pipelineId) => {
    // Clear any existing polling
    if (jobsPollingIntervalRef.current) {
      clearInterval(jobsPollingIntervalRef.current);
    }

    const repositoryUrl = getRepositoryUrl(selectedResource?.data);
    if (!repositoryUrl) return;

    jobsPollingIntervalRef.current = setInterval(async () => {
      try {
        const jobs = await unifiedGitDataService.getWorkflowJobs(
          gitlabProjectId,
          repositoryUrl,
          pipelineId
        );

        if (jobs) {
          setPipelineJobs(jobs);

          // Stop polling if all jobs are completed
          const hasActiveJobs = jobs.some(job => 
            ['running', 'pending'].includes(job.status)
          );

          if (!hasActiveJobs && jobsPollingIntervalRef.current) {
            console.log('All jobs completed, stopping polling'); /* eslint-disable-line no-console */
            clearInterval(jobsPollingIntervalRef.current);
            jobsPollingIntervalRef.current = null;
          }
        }
      } catch (error) {
        console.error('Error polling job statuses:', error); /* eslint-disable-line no-console */
      }
    }, 5000); // Poll every 5 seconds
  };

  // Stop polling when modal closes
  useEffect(() => {
    if (!showPipelineLogsModal && jobsPollingIntervalRef.current) {
      clearInterval(jobsPollingIntervalRef.current);
      jobsPollingIntervalRef.current = null;
    }
  }, [showPipelineLogsModal]);

  // Refresh pipeline jobs manually
  const handleRefreshPipelineJobs = async () => {
    if (!selectedPipelineForLogs) return;

    const gitlabProjectId = getGitlabProjectId(selectedResource?.data);
    if (!gitlabProjectId) return;

    const repositoryUrl = getRepositoryUrl(selectedResource?.data);
    if (!repositoryUrl) return;

    try {
      setLoadingPipelineConfig(true);

      // Fetch jobs for this pipeline
      const jobs = await unifiedGitDataService.getWorkflowJobs(
        gitlabProjectId,
        repositoryUrl,
        selectedPipelineForLogs.id
      );

      setPipelineJobs(jobs || []);

      // Fetch pipeline configuration
      const config = await fetchPipelineConfig(selectedPipelineForLogs.ref);
      if (config) {
        setPipelineConfig(config);
      }

      // Restart polling if pipeline is active
      const hasActiveJobs = jobs?.some(job => 
        ['running', 'pending'].includes(job.status)
      );

      if (hasActiveJobs) {
        startJobsPolling(gitlabProjectId, selectedPipelineForLogs.id);
      } else if (jobsPollingIntervalRef.current) {
        clearInterval(jobsPollingIntervalRef.current);
        jobsPollingIntervalRef.current = null;
      }
    } catch (error) {
      console.error('Failed to refresh pipeline jobs:', error); /* eslint-disable-line no-console */
      alert(`Failed to refresh: ${error.message}`);
    } finally {
      setLoadingPipelineConfig(false);
    }
  };

  // View logs for a specific job
  const handleViewJobLogs = async (job) => {
    const gitlabProjectId = getGitlabProjectId(selectedResource?.data);
    if (!gitlabProjectId) return;

    const repositoryUrl = getRepositoryUrl(selectedResource?.data);
    if (!repositoryUrl) return;

    try {
      setLoadingJobLogs(true);
      setSelectedJobLogs('');

      const logs = await unifiedGitDataService.getJobTrace(
        gitlabProjectId,
        repositoryUrl,
        job.id
      );

      setSelectedJobLogs(logs || 'No logs available');
    } catch (error) {
      console.error('Failed to load job logs:', error); /* eslint-disable-line no-console */
      setSelectedJobLogs(`Error loading logs: ${error.message}`);
    } finally {
      setLoadingJobLogs(false);
    }
  };

  // Fetch and parse pipeline configuration from .gitlab-ci.yml
  const fetchPipelineConfig = async (pipelineRef) => {
    const gitlabProjectId = getGitlabProjectId(selectedResource?.data);
    if (!gitlabProjectId) {
      console.log('No GitLab project ID found'); /* eslint-disable-line no-console */
      return null;
    }

    try {
      setLoadingPipelineConfig(true);
      
      // Use the pipeline's ref (branch/tag) or fall back to current branch
      const ref = pipelineRef || currentBranch || DEFAULT_MICROSERVICE_BRANCH;
      console.log(`Fetching .gitlab-ci.yml from ${gitlabProjectId} @ ${ref}`); /* eslint-disable-line no-console */
      
      const repositoryUrl = getRepositoryUrl(selectedResource?.data);
      if (!repositoryUrl) return null;
      
      // Fetch .gitlab-ci.yml file
      const fileData = await unifiedGitDataService.getFileContent(
        gitlabProjectId,
        repositoryUrl,
        '.gitlab-ci.yml',
        ref
      );

      if (!fileData || !fileData.decodedContent) {
        console.warn('No .gitlab-ci.yml file found or empty content'); /* eslint-disable-line no-console */
        return null;
      }

      // Parse the YAML content
      const parsed = yaml.load(fileData.decodedContent);
      console.log('Parsed pipeline config:', parsed); /* eslint-disable-line no-console */
      
      // Detect CI/CD tools from pipeline config
      const cicdTools = [];
      if (parsed.image) {
        const runtime = detectAllTools({ dockerImage: parsed.image }, {}).runtime;
        if (runtime?.runtime) {
          cicdTools.push(runtime.runtime);
        }
      }
      
      const config = {
        stages: [],
        jobs: [],
        detectedTools: cicdTools
      };

      // Parse jobs first
      Object.keys(parsed).forEach(key => {
        // Skip reserved keywords
        if (['include', 'default', 'variables', 'stages', 'workflow', 'cache'].includes(key)) {
          return;
        }

        const job = parsed[key];
        if (typeof job === 'object' && job !== null && !key.startsWith('.')) {
          config.jobs.push({
            name: key,
            stage: job.stage || 'test',
            script: job.script || []
          });
        }
      });

      // Parse stages - either explicit or extract from jobs
      if (parsed.stages && parsed.stages.length > 0) {
        config.stages = parsed.stages;
      } else {
        // Extract unique stages from jobs in order of appearance
        const stagesFromJobs = [];
        config.jobs.forEach(job => {
          if (job.stage && !stagesFromJobs.includes(job.stage)) {
            stagesFromJobs.push(job.stage);
          }
        });
        config.stages = stagesFromJobs;
      }

      console.log('Final pipeline config:', config); /* eslint-disable-line no-console */
      return config;
    } catch (error) {
      console.error('Failed to load pipeline config:', error); /* eslint-disable-line no-console */
      console.error('Error details:', error.message); /* eslint-disable-line no-console */
      return null;
    } finally {
      setLoadingPipelineConfig(false);
    }
  };

  const tabs = [
    {id: 'preview', icon: 'eye', label: 'Preview'},
    {id: 'code', icon: 'code', label: 'Code'},
    {id: 'cloud', icon: 'package', label: 'Cloud'},
    (showDashboardTab && {id: 'dashboard', icon: 'grid', label: 'Dashboards'}),
    {id: 'ai', icon: 'computer', label: 'AI'},
    (showRequirementsTab && {id: 'requirements', icon: 'list', label: 'Requirements'}),
  ].filter(Boolean);

  return (
    <div className="unified-top-bar">
      {/* Left Side: Tabs (Icon-only when not active, expanded when active) */}
      <div className="unified-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`unified-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => onTabChange(tab.id)}
            title={tab.label}
            type="button"
          >
            <span className="tab-icon">
              <Icon name={tab.icon} size={18} />
            </span>
            {activeTab === tab.id && <span className="tab-label">{tab.label}</span>}
          </button>
        ))}
      </div>

      {/* Center: Dynamic Content Based on Active Tab */}
      <div className="unified-center-content">
        {/* Code Tab: Service Dropdown + Git Controls */}
        {activeTab === 'code' && configuration?.services && (
          <>
            <button
              className="home-button-unified"
              onClick={() => onServiceSelect && onServiceSelect('project-configuration')}
              title="Go to Project Home"
              type="button"
            >
              <Icon name="home" size={16} />
            </button>
            <select
              className="service-dropdown-unified"
              onChange={(e) => onServiceSelect && onServiceSelect(e.target.value)}
              value={selectedResource?.type === 'service' ? selectedResource.data?.name : (selectedResource?.type === 'configuration' ? 'project-configuration' : '')}
            >
              <option value="">Select resources...</option>
              {selectedProject && (
                <option value="project-configuration">
                  project home
                </option>
              )}
              {Object.entries(configuration.services)
                .filter(([_, service]) => service.type !== 'custom-resource' && (service.repositoryUrl || service.repoUrl || service.sshUrl))
                .map(([serviceKey, service]) => (
                  <option key={serviceKey} value={service.name}>
                    {service.name}
                  </option>
                ))}
            </select>

            {/* Display detected tools */}
            {detectedTools && selectedResource?.type === 'service' && (
              <div className="detected-tools-display" style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '12px', marginLeft: '8px' }}>
                {detectedTools.languages.slice(0, 2).map((lang, idx) => {
                  const display = getToolDisplay(lang, 'language');
                  return (
                    <span key={idx} style={{ display: 'flex', alignItems: 'center', gap: '4px' }} title={display.name}>
                      <span>{display.icon}</span>
                    </span>
                  );
                })}
                {detectedTools.frameworks.all.length > 0 && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }} title={detectedTools.frameworks.all.join(', ')}>
                    <span>{getToolDisplay(detectedTools.frameworks.all[0], 'framework').icon}</span>
                  </span>
                )}
                {detectedTools.runtime && (
                  <span style={{ fontSize: '11px', opacity: 0.7 }} title={`Runtime: ${detectedTools.runtime.runtime}`}>
                    {detectedTools.runtime.version}
                  </span>
                )}
              </div>
            )}

            {/* Git Controls - Always visible when on Code tab, enabled only when service is selected */}
            {(selectedResource?.type === 'service' || selectedResource?.type === 'configuration') && (
              enableConsolidatedGitButton && selectedResource?.type === 'service' ? (
                /* New Consolidated Git Actions Button */
                <GitActionsButton
                  projectId={selectedProject?.id}
                  serviceName={selectedResource?.data?.name}
                  repositoryUrl={selectedResource?.data?.repositoryUrl || selectedResource?.data?.repoUrl || selectedResource?.data?.sshUrl}
                  currentBranch={currentBranch}
                  currentTag={null}
                  branches={branches.map(b => b.name || b)}
                  tags={tags.map(t => t.name || t)}
                  hasModifications={hasModifications}
                  modifiedFilesCount={modifiedFilesCount}
                  commitsToPush={commitsToPush}
                  commitsToPull={commitsToPull}
                  pipelineStatus={pipelineStatus}
                  onBranchChange={(ref, type) => handleBranchChange(ref, type)}
                  onPushComplete={() => {
                    if (onRefresh) onRefresh();
                    setModifiedFilesCount(0);
                  }}
                  onPullComplete={() => {
                    if (onRefresh) onRefresh();
                    setModifiedFilesCount(0);
                  }}
                  onRefresh={async () => {
                    const gitlabProjectId = getGitlabProjectId(selectedResource?.data);
                    const repositoryUrl = getRepositoryUrl(selectedResource?.data);
                    if (gitlabProjectId && repositoryUrl) {
                      try {
                        const branchList = await unifiedGitDataService.listBranches(gitlabProjectId, repositoryUrl);
                        setBranches(branchList);
                        try {
                          const tagList = await unifiedGitDataService.listTags(gitlabProjectId, repositoryUrl);
                          setTags(tagList);
                        } catch (e) {
                          console.log('Tags not available:', e.message); /* eslint-disable-line no-console */
                        }
                      } catch (error) {
                        console.error('Failed to refresh refs:', error); /* eslint-disable-line no-console */
                      }
                    }
                    if (onRefresh) onRefresh();
                  }}
                  disabled={isPulling || isPushing}
                />
              ) : (
              <div className="git-controls-unified">
                {/* Branch/Tag Selector */}
                <div className="branch-selector-container" ref={branchDropdownRef}>
                  <button
                    className="branch-selector"
                    disabled={loadingRefs}
                    onClick={() => setShowBranchDropdown(!showBranchDropdown)}
                    title="Select branch or tag"
                    type="button"
                  >
                    <Icon name="git-branch" size={14} />
                    <span className="branch-name">{currentBranch || '…'}</span>
                    {hasModifications && <span className="modified-indicator">●</span>}
                    <span className="dropdown-arrow">{showBranchDropdown ? '▲' : '▼'}</span>
                  </button>
                  
                  {showBranchDropdown && (
                    <div className="branch-dropdown">
                      {/* Branches Section */}
                      <div className="branch-dropdown-section-title">
                        🌿 Branches
                        <button
                          className="create-ref-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowBranchDropdown(false);
                            setShowCreateBranch(true);
                          }}
                          type="button"
                          title="Create new branch"
                        >
                          +
                        </button>
                      </div>
                      {branches.length > 0 && branches.map((branch) => (
                        <button
                          className={`branch-option ${branch.name === currentBranch ? 'active' : ''}`}
                          key={`branch-${branch.name}`}
                          onClick={() => handleBranchChange(branch.name, 'branch')}
                          type="button"
                        >
                          <span className="branch-name">{branch.name}</span>
                          {branch.default && <span className="default-badge">DEFAULT</span>}
                        </button>
                      ))}
                      
                      {/* Tags Section */}
                      <div className="branch-dropdown-section-title">
                        🏷️ Tags
                        <button
                          className="create-ref-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowBranchDropdown(false);
                            setShowCreateTag(true);
                          }}
                          type="button"
                          title="Create new tag"
                        >
                          +
                        </button>
                      </div>
                      {tags.length > 0 && tags.map((tag) => (
                        <button
                          className={`branch-option ${tag.name === currentBranch ? 'active' : ''}`}
                          key={`tag-${tag.name}`}
                          onClick={() => handleBranchChange(tag.name, 'tag')}
                          type="button"
                        >
                          <span className="branch-name">{tag.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pull button */}
                <button
                  className="unified-action-btn"
                  onClick={() => {
                    const startTime = Date.now();
                    if (window.analytics?.trackAction) {
                      window.analytics.trackAction('git', 'pull_started', { branch: currentBranch });
                    }
                    
                    // Wrap the onPull to add completion tracking
                    const originalOnPull = onPull;
                    const trackedPull = async () => {
                      try {
                        await originalOnPull();
                        if (window.analytics?.trackAction) {
                          window.analytics.trackAction('git', 'pull', {
                            branch: currentBranch,
                            duration: Date.now() - startTime
                          });
                        }
                        if (window.analytics?.trackFeature) {
                          window.analytics.trackFeature('git_pull', {
                            branch: currentBranch
                          });
                        }
                      } catch (error) {
                        if (window.analytics?.trackError) {
                          window.analytics.trackError('git_error', {
                            operation: 'pull',
                            error: error.message
                          });
                        }
                        throw error;
                      }
                    };
                    trackedPull();
                  }}
                  disabled={isPulling}
                  title="Pull from repository"
                  type="button"
                >
                  <Icon name="download" size={16} />
                  <span>{isPulling ? 'Pulling...' : 'Pull'}</span>
                </button>

                {/* Push button */}
                <button
                  className="unified-action-btn primary"
                  onClick={() => {
                    const startTime = Date.now();
                    if (window.analytics?.trackAction) {
                      window.analytics.trackAction('git', 'push_started', { branch: currentBranch });
                    }
                    
                    // Wrap the onPush to add completion tracking
                    const originalOnPush = onPush;
                    const trackedPush = async () => {
                      try {
                        await originalOnPush();
                        if (window.analytics?.trackAction) {
                          window.analytics.trackAction('git', 'push', {
                            branch: currentBranch,
                            duration: Date.now() - startTime
                          });
                        }
                        if (window.analytics?.trackFeature) {
                          window.analytics.trackFeature('git_push', {
                            branch: currentBranch
                          });
                        }
                      } catch (error) {
                        if (window.analytics?.trackError) {
                          window.analytics.trackError('git_error', {
                            operation: 'push',
                            error: error.message
                          });
                        }
                        throw error;
                      }
                    };
                    trackedPush();
                  }}
                  disabled={!hasModifications || isPushing}
                  title="Push changes"
                  type="button"
                >
                  <Icon name="upload" size={16} />
                  <span>{isPushing ? 'Pushing...' : 'Push'}</span>
                </button>

                {/* Pipeline button */}
                <button
                  className={`unified-action-btn pipeline-btn-${pipelineStatus || 'default'}`}
                  onClick={handleViewPipelines}
                  title="View pipeline history"
                  type="button"
                >
                  {pipelineStatus === 'success' && (
                    <Icon name="check-circle" size={16} color="#2ecc71" />
                  )}
                  {pipelineStatus === 'failed' && (
                    <Icon name="warning" size={16} color="#e74c3c" />
                  )}
                  {(pipelineStatus === 'running' || pipelineStatus === 'pending') && (
                    <Icon name="spinner" size={16} color="#3498db" />
                  )}
                  {!pipelineStatus && (
                    <Icon name="settings" size={16} />
                  )}
                  <span>Pipelines</span>
                </button>
              </div>
              )
            )}

            {/* Clone / Code dropdown - shown when a service with a repo URL is selected */}
            {(selectedResource?.type === 'service' || selectedResource?.type === 'configuration') && (() => {
              const repoUrl = getRepositoryUrl(selectedResource.data);
              if (!repoUrl) return null;
              const sshUrl = repoUrl.replace('https://', 'git@').replace(/\.git$/, '.git').replace(/\//, ':');
              const repoFolder = repoUrl.split('/').pop()?.replace(/\.git$/, '') || 'repo';
              return (
                <div className="repo-dropdown-wrapper repo-dropdown-wrapper--right" ref={cloneDropdownRef} style={{ marginLeft: '4px' }}>
                  <button
                    className="unified-action-btn"
                    onClick={() => setShowCloneDropdown(!showCloneDropdown)}
                    title="Clone or open repository"
                    type="button"
                  >
                    <Icon name="code" size={16} />
                    <Icon name="chevron-down" size={12} />
                  </button>
                  {showCloneDropdown && (
                    <div className="repo-dropdown-menu">
                      <div className="repo-dropdown-section">
                        <div className="repo-dropdown-section-title">{'Clone'}</div>
                        <div className="repo-dropdown-clone-item">
                          <input
                            className="repo-clone-input"
                            onClick={(e) => e.target.select()}
                            readOnly
                            title="SSH"
                            type="text"
                            value={sshUrl}
                          />
                          <button
                            className="repo-copy-button"
                            onClick={() => navigator.clipboard.writeText(sshUrl)}
                            title="Copy SSH URL"
                            type="button"
                          >
                            <Icon name="document" size={14} />
                          </button>
                        </div>
                        <div className="repo-dropdown-clone-item">
                          <input
                            className="repo-clone-input"
                            onClick={(e) => e.target.select()}
                            readOnly
                            title="HTTPS"
                            type="text"
                            value={repoUrl}
                          />
                          <button
                            className="repo-copy-button"
                            onClick={() => navigator.clipboard.writeText(repoUrl)}
                            title="Copy HTTPS URL"
                            type="button"
                          >
                            <Icon name="document" size={14} />
                          </button>
                        </div>
                      </div>

                      <div className="repo-dropdown-section">
                        <div className="repo-dropdown-section-title">{'Open in Claude Code'}</div>
                        <div className="repo-dropdown-clone-item">
                          <input
                            className="repo-clone-input"
                            onClick={(e) => e.target.select()}
                            readOnly
                            title="SSH: Run this in your terminal to clone via SSH and open in Claude Code"
                            type="text"
                            value={`git clone ${sshUrl} && cd ${repoFolder} && claude`}
                          />
                          <button
                            className="repo-copy-button"
                            onClick={() => navigator.clipboard.writeText(`git clone ${sshUrl} && cd ${repoFolder} && claude`)}
                            title="Copy SSH command to clipboard"
                            type="button"
                          >
                            <Icon name="document" size={14} />
                          </button>
                        </div>
                        <div className="repo-dropdown-clone-item">
                          <input
                            className="repo-clone-input"
                            onClick={(e) => e.target.select()}
                            readOnly
                            title="HTTPS: Run this in your terminal to clone via HTTPS and open in Claude Code"
                            type="text"
                            value={`git clone ${repoUrl} && cd ${repoFolder} && claude`}
                          />
                          <button
                            className="repo-copy-button"
                            onClick={() => navigator.clipboard.writeText(`git clone ${repoUrl} && cd ${repoFolder} && claude`)}
                            title="Copy HTTPS command to clipboard"
                            type="button"
                          >
                            <Icon name="document" size={14} />
                          </button>
                        </div>
                      </div>

                      <div className="repo-dropdown-section">
                        <div className="repo-dropdown-section-title">{'Open in GitHub Copilot'}</div>
                        <div className="repo-dropdown-clone-item">
                          <input
                            className="repo-clone-input"
                            onClick={(e) => e.target.select()}
                            readOnly
                            title="SSH: Run this in your terminal to clone via SSH and open in GitHub Copilot"
                            type="text"
                            value={`git clone ${sshUrl} && cd ${repoFolder} && code .`}
                          />
                          <button
                            className="repo-copy-button"
                            onClick={() => navigator.clipboard.writeText(`git clone ${sshUrl} && cd ${repoFolder} && code .`)}
                            title="Copy SSH command to clipboard"
                            type="button"
                          >
                            <Icon name="document" size={14} />
                          </button>
                        </div>
                        <div className="repo-dropdown-clone-item">
                          <input
                            className="repo-clone-input"
                            onClick={(e) => e.target.select()}
                            readOnly
                            title="HTTPS: Run this in your terminal to clone via HTTPS and open in GitHub Copilot"
                            type="text"
                            value={`git clone ${repoUrl} && cd ${repoFolder} && code .`}
                          />
                          <button
                            className="repo-copy-button"
                            onClick={() => navigator.clipboard.writeText(`git clone ${repoUrl} && cd ${repoFolder} && code .`)}
                            title="Copy HTTPS command to clipboard"
                            type="button"
                          >
                            <Icon name="document" size={14} />
                          </button>
                        </div>
                      </div>

                      <div className="repo-dropdown-section">
                        <div className="repo-dropdown-section-title">{'Open in your IDE'}</div>
                        <div className="repo-dropdown-links">
                          <a
                            className="repo-dropdown-link"
                            href={`vscode://vscode.git/clone?url=${encodeURIComponent(sshUrl)}`}
                            rel="noopener noreferrer"
                          >
                            {'Visual Studio Code (SSH)'}
                          </a>
                          <a
                            className="repo-dropdown-link"
                            href={`vscode://vscode.git/clone?url=${encodeURIComponent(repoUrl)}`}
                            rel="noopener noreferrer"
                          >
                            {'Visual Studio Code (HTTPS)'}
                          </a>
                          <a
                            className="repo-dropdown-link"
                            href={`jetbrains://idea/checkout/git?idea.required.plugins.id=Git4Idea&checkout.repo=${encodeURIComponent(sshUrl)}`}
                            rel="noopener noreferrer"
                          >
                            {'IntelliJ IDEA (SSH)'}
                          </a>
                          <a
                            className="repo-dropdown-link"
                            href={`jetbrains://idea/checkout/git?idea.required.plugins.id=Git4Idea&checkout.repo=${encodeURIComponent(repoUrl)}`}
                            rel="noopener noreferrer"
                          >
                            {'IntelliJ IDEA (HTTPS)'}
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        )}

        {/* Preview Tab: Service Dropdown + Refresh */}
        {activeTab === 'preview' && configuration?.services && (
          <>
            <button
              className="home-button-unified"
              onClick={() => onServiceSelect && onServiceSelect('project-configuration')}
              title="Go to Project Home"
              type="button"
            >
              <Icon name="home" size={16} />
            </button>
            <select
              className="service-dropdown-unified"
              onChange={(e) => {
                const value = e.target.value;
                if (onServiceSelect) {
                  onServiceSelect(value);
                  // Reset dropdown to placeholder after special actions
                  if (value === 'canvas' || value === '+create-service') {
                    setTimeout(() => {
                      e.target.value = '';
                    }, 0);
                  }
                }
              }}
              value={selectedResource?.type === 'service' ? selectedResource.data?.name : (selectedResource?.type === 'configuration' ? 'project-configuration' : '')}
            >
              <option value="">Select service to preview...</option>
              {selectedProject && (
                <option value="project-configuration">
                  project home
                </option>
              )}
              {Object.entries(configuration.services)
                .filter(([_, service]) => service.type !== 'custom-resource' && (service.repositoryUrl || service.repoUrl || service.sshUrl))
                .map(([serviceKey, service]) => (
                  <option key={serviceKey} value={service.name}>
                    {service.name}
                  </option>
                ))}
              <option value="+create-service" className="dropdown-separator-top">+ Create new service</option>
            </select>
            
            {selectedResource?.type === 'service' && (
              <>           
                <button
                  className="unified-action-btn"
                  onClick={() => onRefresh(forceCleanup)}
                  title={forceCleanup ? "Force cleanup and refresh preview" : "Refresh preview"}
                  type="button"
                >
                  <Icon name="refresh" size={16} />
                  <span>Refresh</span>
                </button>
                <label className="force-cleanup-checkbox" title="Force cleanup kills all preview processes and ports before restarting">
                  <input
                    type="checkbox"
                    checked={forceCleanup}
                    onChange={(e) => setForceCleanup(e.target.checked)}
                  />
                  <span>Clean</span>
                </label>
              </>
            )}
          </>
        )}

        {/* Cloud Tab: Preview button */}
        {activeTab === 'cloud' && selectedResource?.type === 'service' && (
          <button
            className="unified-action-btn primary"
            onClick={onPreview}
            title="Preview service"
            type="button"
          >
            <Icon name="eye" size={16} />
            <span>Preview</span>
          </button>
        )}

        {/* Dashboard Tab: Project name */}
        {activeTab === 'dashboard' && selectedProject && (
          <div className="project-name-display">
            <Icon name="folder" size={16} />
            <span>{selectedProject.name}</span>
          </div>
        )}
      </div>

      {/* Right Side: View dropdown, Deploy dropdown + Fullscreen toggle */}
      <div className="unified-controls">
        {/* View Mode Dropdown - Show on Code and Cloud tabs when preview is available */}
        {(activeTab === 'code' || activeTab === 'cloud') && previewTabs.length > 0 && previewTabs.some(tab => tab.url) && (
          <div className="view-dropdown-container" ref={viewDropdownRef}>
            <button
              className="unified-action-btn view-btn"
              onClick={() => setShowViewDropdown(!showViewDropdown)}
              title="Change view mode"
              type="button"
            >
              <Icon name="split" size={16} />
              <span>View: {splitViewMode === 'editor' ? 'Code' : splitViewMode === 'split' ? 'Split' : 'Preview'}</span>
              <Icon name="chevron-down" size={12} />
            </button>
            
            {showViewDropdown && (
              <div className="view-dropdown-menu">
                <div className="view-dropdown-header">View Mode</div>
                <button
                  className={`view-dropdown-item ${splitViewMode === 'editor' ? 'active' : ''}`}
                  onClick={() => {
                    onSplitViewModeChange?.('editor');
                    setShowViewDropdown(false);
                  }}
                  type="button"
                >
                  <Icon name="code" size={16} />
                  <span>Code Only</span>
                </button>
                <button
                  className={`view-dropdown-item ${splitViewMode === 'split' ? 'active' : ''}`}
                  onClick={() => {
                    onSplitViewModeChange?.('split');
                    setShowViewDropdown(false);
                  }}
                  type="button"
                >
                  <Icon name="split" size={16} />
                  <span>Split View</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Deploy Dropdown - Show on Code, Preview, and Cloud tabs */}
        {(activeTab === 'code' || activeTab === 'preview' || activeTab === 'cloud') && configuration?.environments && configuration.environments.length > 0 && (
          <div className="deploy-dropdown-container" ref={deployDropdownRef}>
            <button
              className={`unified-action-btn deploy-btn ${isDeploying ? 'deploying' : ''}`}
              onClick={() => setShowDeployDropdown(!showDeployDropdown)}
              disabled={isDeploying}
              title="Deploy to environment"
              type="button"
            >
              <Icon name="rocket" size={16} />
              <span>{isDeploying ? 'Deploying...' : 'Deploy'}</span>
              <Icon name="chevron-down" size={12} />
            </button>
            
            {showDeployDropdown && (
              <div className="deploy-dropdown-menu">
                <div className="deploy-dropdown-header">Deploy to Environment</div>
                {configuration.environments.map((env, idx) => (
                  <div key={idx} className="deploy-dropdown-item">
                    <div className="deploy-env-info">
                      <div className="deploy-env-name">{env.label || env.envId}</div>
                      {env.hosts && env.hosts.length > 0 && (
                        <div className="deploy-env-urls">
                          {env.hosts.map((hostObj, hIdx) => (
                            <a
                              key={hIdx}
                              href={`${hostObj.scheme || 'https'}://${hostObj.host}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="deploy-env-url"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {hostObj.host}
                              <Icon name="link" size={12} />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      className="deploy-env-btn"
                      onClick={() => handleDeploy(env)}
                      disabled={isDeploying}
                      type="button"
                    >
                      Deploy
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Real-time Collaboration Panel */}
        {localStorage.getItem('enableCollaboration') === 'true' && selectedProject?.id && user && (
          <CollaborationPanel
            projectId={selectedProject.id}
            userId={user.id || user.sub || user.email}
            username={user.name || user.email || 'Anonymous'}
          />
        )}
        
        <button
          className="unified-control-btn"
          onClick={onToggleFullscreen}
          title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          type="button"
        >
          <Icon name={isFullscreen ? 'exit-fullscreen' : 'fullscreen'} size={18} />
        </button>
      </div>

      {/* Create Branch Modal */}
      {showCreateBranch && createPortal(
        <div className="modal-overlay" onClick={() => setShowCreateBranch(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Branch</h3>
            <div className="modal-form">
              <label>
                Branch Name:
                <input
                  type="text"
                  value={newBranchName}
                  onChange={(e) => setNewBranchName(e.target.value)}
                  placeholder="feature/my-new-branch"
                  autoFocus
                />
              </label>
              <p className="modal-info">
                Branch will be created from: <strong>{currentBranch}</strong>
              </p>
              <div className="modal-actions">
                <button
                  onClick={handleCreateBranch}
                  disabled={!newBranchName.trim() || isCreating}
                  type="button"
                  className="btn-primary"
                >
                  {isCreating ? 'Creating...' : 'Create Branch'}
                </button>
                <button
                  onClick={() => {
                    setShowCreateBranch(false);
                    setNewBranchName('');
                  }}
                  disabled={isCreating}
                  type="button"
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Create Tag Modal */}
      {showCreateTag && createPortal(
        <div className="modal-overlay" onClick={() => setShowCreateTag(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Tag</h3>
            <div className="modal-form">
              <label>
                Tag Name:
                <input
                  type="text"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="v1.0.0"
                  autoFocus
                />
              </label>
              <label>
                Message (optional):
                <textarea
                  value={tagMessage}
                  onChange={(e) => setTagMessage(e.target.value)}
                  placeholder="Release notes..."
                  rows="3"
                />
              </label>
              <p className="modal-info">
                Tag will be created from: <strong>{currentBranch}</strong>
              </p>
              <div className="modal-actions">
                <button
                  onClick={handleCreateTag}
                  disabled={!newTagName.trim() || isCreating}
                  type="button"
                  className="btn-primary"
                >
                  {isCreating ? 'Creating...' : 'Create Tag'}
                </button>
                <button
                  onClick={() => {
                    setShowCreateTag(false);
                    setNewTagName('');
                    setTagMessage('');
                  }}
                  disabled={isCreating}
                  type="button"
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Pipeline Logs Modal */}
      {showPipelineModal && currentPipeline && createPortal(
        <div className="modal-overlay" onClick={() => setShowPipelineModal(false)}>
          <div className="pipeline-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="pipeline-modal-header">
              <h3>🚀 Deployment Pipeline</h3>
              <button 
                className="modal-close-btn"
                onClick={() => setShowPipelineModal(false)}
                type="button"
              >
                ×
              </button>
            </div>
            <div className="pipeline-modal-info">
              <div><strong>Environment:</strong> {currentPipeline.environmentLabel}</div>
              <div><strong>Pipeline ID:</strong> {currentPipeline.id}</div>
              {currentPipeline.url && (
                <div>
                  <strong>GitLab:</strong>{' '}
                  <a 
                    href={currentPipeline.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="pipeline-gitlab-link"
                  >
                    View in Tool <Icon name="link" size={12} />
                  </a>
                </div>
              )}
            </div>
            <div className="pipeline-logs-container">
              {pipelineLogs.map((log, idx) => (
                <div key={idx} className="pipeline-log-entry">{log}</div>
              ))}
            </div>
            <div className="pipeline-modal-actions">
              <button
                onClick={() => setShowPipelineModal(false)}
                type="button"
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Pipeline History Modal */}
      {showPipelineHistoryModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowPipelineHistoryModal(false)}>
          <div className="pipeline-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="pipeline-modal-header">
              <h3>🔧 Pipeline History - {currentBranch}</h3>
              <div className="pipeline-modal-header-actions">
                <button
                  className="pipeline-refresh-btn"
                  onClick={handleRefreshPipelines}
                  disabled={loadingPipelines}
                  type="button"
                  title="Refresh pipeline list"
                >
                  🔄
                </button>
                <button
                  className="pipeline-close-btn"
                  onClick={() => setShowPipelineHistoryModal(false)}
                  type="button"
                >
                  ×
                </button>
              </div>
            </div>
            
            {loadingPipelines ? (
              <div className="pipeline-loading">Loading pipelines...</div>
            ) : pipelineHistory.length === 0 ? (
              <div className="pipeline-empty">No pipelines found for this branch</div>
            ) : (
              <div className="pipeline-list">
                {pipelineHistory.map(pipeline => {
                  const statusEmoji = {
                    'success': '✅',
                    'failed': '❌',
                    'running': '🔄',
                    'pending': '⏳',
                    'canceled': '🚫',
                    'skipped': '⏭️'
                  }[pipeline.status] || '❓';

                  return (
                    <div key={pipeline.id} className="pipeline-item">
                      <div className="pipeline-item-header">
                        <span className={`pipeline-status-badge ${pipeline.status}`}>
                          {statusEmoji} {pipeline.status}
                        </span>
                        <span className="pipeline-id">#{pipeline.id}</span>
                      </div>
                      <div className="pipeline-item-details">
                        <div><strong>Ref:</strong> {pipeline.ref}</div>
                        <div><strong>Created:</strong> {new Date(pipeline.created_at).toLocaleString()}</div>
                        {pipeline.updated_at && (
                          <div><strong>Updated:</strong> {new Date(pipeline.updated_at).toLocaleString()}</div>
                        )}
                        {pipeline.web_url && (
                          <div>
                            <a 
                              href={pipeline.web_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="pipeline-gitlab-link"
                            >
                              View in Tool <Icon name="link" size={12} />
                            </a>
                          </div>
                        )}
                      </div>
                      <button
                        className="btn-primary"
                        onClick={() => handleViewPipelineLogs(pipeline)}
                        type="button"
                      >
                        View Jobs & Logs
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            
          </div>
        </div>,
        document.body
      )}

      {/* Pipeline Jobs & Logs Modal */}
      {showPipelineLogsModal && selectedPipelineForLogs && createPortal(
        <div className="modal-overlay" onClick={() => setShowPipelineLogsModal(false)}>
          <div className={`pipeline-logs-modal-content ${document.body.classList.contains('dark-mode') ? 'dark-mode' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="pipeline-modal-header">
              <h3>📋 Pipeline #{selectedPipelineForLogs.id} - Jobs & Logs</h3>
              <div className="pipeline-modal-header-actions">
                <button
                  className="pipeline-refresh-btn"
                  onClick={handleRefreshPipelineJobs}
                  disabled={loadingPipelineConfig}
                  type="button"
                  title="Refresh pipeline jobs"
                >
                  🔄
                </button>
                <button
                  className="pipeline-close-btn"
                  onClick={() => setShowPipelineLogsModal(false)}
                  type="button"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Pipeline Visualization */}
            <PipelineVisualization
              pipelineConfig={pipelineConfig}
              pipelineJobs={pipelineJobs}
              loadingPipelineConfig={loadingPipelineConfig}
              onJobClick={handleViewJobLogs}
            />
            
            <div className="pipeline-logs-split-view">
              {/* Jobs List */}
              <div className="jobs-list-panel">
                <h4>Jobs</h4>
                {pipelineJobs.length === 0 ? (
                  <div className="jobs-empty">No jobs found</div>
                ) : (
                  <div className="jobs-list">
                    {pipelineJobs.map(job => {
                      const statusEmoji = {
                        'success': '✅',
                        'failed': '❌',
                        'running': '🔄',
                        'pending': '⏳',
                        'canceled': '🚫',
                        'skipped': '⏭️'
                      }[job.status] || '❓';

                      return (
                        <div 
                          key={job.id} 
                          className="job-item"
                          onClick={() => handleViewJobLogs(job)}
                        >
                          <div className="job-name">
                            {statusEmoji} {job.name}
                          </div>
                          <div className="job-stage">{job.stage}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Job Logs */}
              <div className="job-logs-panel">
                <h4>Logs</h4>
                {loadingJobLogs ? (
                  <div className="logs-loading">Loading logs...</div>
                ) : selectedJobLogs ? (
                  <pre className="job-logs-content">{selectedJobLogs}</pre>
                ) : (
                  <div className="logs-empty">Select a job to view logs</div>
                )}
              </div>
            </div>
            
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

UnifiedTopBar.propTypes = {
  activeTab: PropTypes.string.isRequired,
  configuration: PropTypes.object,
  hasModifications: PropTypes.bool,
  isFullscreen: PropTypes.bool,
  isPulling: PropTypes.bool,
  isPushing: PropTypes.bool,
  markdownFiles: PropTypes.array,
  onMarkdownFileSelect: PropTypes.func,
  onNewConversation: PropTypes.func,
  onPipeline: PropTypes.func,
  onPreview: PropTypes.func,
  onPull: PropTypes.func,
  onPush: PropTypes.func,
  onRefresh: PropTypes.func,
  onServiceSelect: PropTypes.func,
  onShowHistory: PropTypes.func,
  onSplitViewModeChange: PropTypes.func,
  onTabChange: PropTypes.func.isRequired,
  onToggleFullscreen: PropTypes.func.isRequired,
  pipelineStatus: PropTypes.string,
  previewTabs: PropTypes.array,
  selectedMarkdownFile: PropTypes.string,
  selectedProject: PropTypes.object,
  selectedResource: PropTypes.object,
  showDashboardTab: PropTypes.bool,
  showRequirementsTab: PropTypes.bool,
  splitViewMode: PropTypes.oneOf(['editor', 'split', 'preview']),
};

export default UnifiedTopBar;
